import api from "@/helpers/api";
import { getSubCategories } from "@/helpers/categories.js";
import { Store } from "vuex";

const state = {
  categoryList: [],
  isCategoriesLoading: false,
  isFetchingCategoryProducts: false,
};

const getters = {
  categoryList: (state) => state.categoryList,
  isFetchingCategoryProducts: (state) => state.isFetchingCategoryProducts,
  categoryTree: (state) => {
    const categories = state.categoryList;
    const categoryTree = categories
      .filter((cat) => !cat.parent?.length)
      .map((category) => ({
        ...getSubCategories(categories, category),
      }));

    return categoryTree;
  },
};

const actions = {
  /***************
   * @Action : fetchCategories
   * @Desc : fetch all product categories
   ***************/
  fetchCategories: async (state) => {
    if (state.getters.categoryList.length > 0) {
      return;
    }

    try {
      state.isCategoriesLoading = true;
      const data = await api.post("/categories", ["Rofix_Products"]);
      const categories = data.categoryTrees[0].categories;
      /// const categories = [...data[0]?.categories];
      state.commit("setCategoryList", categories);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  /***************
   * @Action : fetchInitialProducts
   * @Desc : fetch all initial product chunks for a given category
   ***************/
  fetchInitialProducts: async (state, { identifier }) => {
    state.commit("setFetchingCategoryProducts", true);
    const category = state.getters.categoryList.find(
      (cat) => cat.identifier === identifier
    );

    if (category?.products) {
      state.commit("setFetchingCategoryProducts", false);
      return;
    }

    try {
      const productList = await state.dispatch("fetchProducts", {
        filters: {
          categoryIdentifier: [identifier],
        },
      });
      state.commit("setCategoryProducts", { productList, identifier });
      state.commit("setFetchingCategoryProducts", false);
    } catch (err) {
      state.commit("setFetchingCategoryProducts", false);
      console.log(err);
      throw err;
    }
  },

  /***************
   * @Action : fetchCategoryProductChunk
   * @Desc : fetch a specific chunk of category products
   ***************/
  fetchCategoryProductChunk: async (state, { identifier, chunk }) => {
    state.commit("setFetchingCategoryProducts", true);
    try {
      const category = state.getters.categoryList.find(
        (cat) => cat.identifier === identifier
      );

      const productList = await state.dispatch("fetchProductChunks", {
        ids: category.products.chunks[chunk].ids,
      });

      state.commit("setCategoryProducts", {
        productList,
        identifier,
        chunks: [chunk],
      });
      state.commit("setFetchingCategoryProducts", false);
    } catch (err) {
      state.commit("setFetchingCategoryProducts", false);
      console.log(err);
      throw err;
    }
  },

  /***************
   * @Action : filterCategoryProducts
   * @Desc : fetches a certain product/s based on a search string
   ***************/
  filterCategoryProducts: async (state, payload = {}) => {
    state.commit("setFetchingCategoryProducts", true);
    const { identifier, searchString, language = "en", chunk } = payload;

    if (!searchString) {
      state.commit("clearFilteredCategoryProducts", { identifier });
      state.commit("setFetchingCategoryProducts", false);
      return;
    }

    const category = state.getters.categoryList.find(
      (cat) => cat.identifier === identifier
    );

    const searchObj = searchString
      ? {
          descriptionShort: {
            [language]: searchString,
          },
        }
      : {};

    try {
      let productList;

      if (chunk) {
        productList = await state.dispatch("fetchProductChunks", {
          ids: category.filteredProducts.chunks[chunk].ids,
        });
      } else {
        productList = await state.dispatch("fetchProducts", {
          filters: {
            ...searchObj,
            categoryIdentifier: identifier,
          },
        });
      }

      state.commit("setCategoryProducts", {
        productList,
        fromSearch: searchString.length,
        identifier,
        chunks: chunk ? [chunk] : [],
      });
      state.commit("setFetchingCategoryProducts", false);
    } catch (err) {
      state.commit("setFetchingCategoryProducts", false);
      console.log(err);
      throw err;
    }
  },
};

const mutations = {
  setCategoryList(state, payload = {}) {
    state.categoryList = payload;
  },
  setFetchingCategoryProducts(state, isFetching) {
    state.isFetchingCategoryProducts = isFetching;
  },
  setCategoryProducts: (state, payload) => {
    const { productList, identifier, fromSearch, chunks } = payload;

    state.categoryList = state.categoryList.map((category) => {
      const newCategory = { ...category };
      const productKey = !fromSearch ? "products" : "filteredProducts";

      if (category.identifier !== identifier) {
        return newCategory;
      }

      if (!fromSearch && newCategory.filteredProducts) {
        newCategory.filteredProducts = null;
      }

      if (chunks?.length) {
        chunks.forEach((chunkIndex) => {
          newCategory[productKey].chunks[chunkIndex] = productList.chunks?.[
            chunkIndex
          ] || {
            ...newCategory[productKey].chunks[chunkIndex],
            ...productList,
            loaded: true,
          };
        });
      } else {
        newCategory[productKey] = productList;
      }

      return newCategory;
    });
  },
  clearFilteredCategoryProducts: (state, { identifier }) => {
    const categoryIndex = state.categoryList.findIndex(
      (cat) => cat.identifier === identifier
    );

    if (categoryIndex < 0) {
      return;
    }

    state.categoryList[categoryIndex].filteredProducts = null;
    //state.categoryList = state.categoryList.map((category) => {});
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
